import { LogIn } from "lucide-react";
import { useAuth } from "@/features/auth/hooks/useAuth";

interface RateLimitDialogProps {
    show: boolean;
    message: string;
    resetAt?: number; // Unix timestamp
    onLogin: () => void;
    onClose: () => void;
    backgroundImage?: string;
}

export function RateLimitDialog({
    show,
    message,
    resetAt,
    onLogin,
    onClose,
    backgroundImage,
}: RateLimitDialogProps) {
    const { isAuthenticated } = useAuth();

    if (!show) return null;

    // Calculate the reset time in a human-readable format
    const getResetTimeText = () => {
        if (!resetAt) return "";

        const now = Math.floor(Date.now() / 1000);
        const diff = resetAt - now;

        if (diff <= 0) return "soon";

        const hours = Math.floor(diff / 3600);
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `in ${days} day${days > 1 ? "s" : ""} (${hours % 24} hours)`;
        }
        return `in ${hours} hour${hours > 1 ? "s" : ""}`;
    };

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-label="Rate limit warning"
            style={{
                backgroundImage: backgroundImage
                    ? `linear-gradient(rgba(255,243,234,0.92), rgba(255,243,234,0.92)), url(${backgroundImage})`
                    : undefined,
                backgroundColor: backgroundImage ? undefined : "rgba(0,0,0,0.5)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center bottom",
            }}
        >
            <div className="bg-white/95 backdrop-blur-sm border border-[#F2E8E2] rounded-2xl shadow-xl w-[90%] max-w-md px-6 py-8 text-center">
                {/* Warning Icon */}
                <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                    <svg
                        className="w-8 h-8 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                </div>

                <h3 className="text-slate-800 font-extrabold text-xl mb-2 !hidden">
                    Rate Limit Reached
                </h3>

                <p className="text-slate-600 mb-1 !hidden">{message}</p>

                {resetAt ? (
                    <p className="text-slate-500 text-sm mb-5">
                        You can create more plans {getResetTimeText()}
                    </p>
                ) : null}

                {/* Only show the login promo if user is NOT authenticated */}
                {!isAuthenticated && (
                    <>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                            <p className="text-slate-700 text-sm font-semibold mb-2">
                                Rate Limit Reached
                            </p>
                            <p className="text-slate-600 text-sm">
                                {message}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={onLogin}
                                className="w-full h-12 rounded-lg bg-[#ED6F2E] hover:bg-[#e36522] text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                            >
                                <LogIn size={18} />
                                Log In to Continue
                            </button>
                            <button
                                onClick={onClose}
                                className="w-full h-11 rounded-lg border border-[#E5DED8] text-slate-700 font-semibold bg-white hover:bg-slate-50 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}

                {/* If user IS authenticated, just show close button */}
                {isAuthenticated && (
                    <div className="mt-4">
                        <button
                            onClick={onClose}
                            className="w-full h-11 rounded-lg border border-[#E5DED8] text-slate-700 font-semibold bg-white hover:bg-slate-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
